import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { WifiWizard2 } from '@ionic-native/wifi-wizard-2/ngx';
import { AndroidPermissions } from '@ionic-native/android-permissions/ngx';
import { delay, map, mergeMap, retryWhen } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ApiBasicService } from './api/api-basic.service';
import { ConnectionService } from './api/connection.service';
import { ApiMidService } from './api/api-mid.service';
import { State, StateInterface } from '../interfaces/biozoom-api/app-configuration';
import { UsbApiService } from './scanner/usb-api.service';
import { UsbApiService2 } from './scanner/usb-api2.service';

@Injectable({
  providedIn: 'root'
})
export class NetworkService {
  // private wifi: string;
  private password = 'biozoom.net';
  // private demoMode = false;
  // private deviceId: string;

  constructor(private platform: Platform,
    private wifiWizard2: WifiWizard2,
    private androidPermissions: AndroidPermissions,
    private apiBasicService: ApiBasicService,
    private apiMidService: ApiMidService,
    private http: HttpClient,
    // private stateService: StateService
  ) {
    // this.stateService.initialized.subscribe((initialized) => {
    //     if (initialized) {
    //         this.initialize();
    //     }
    // });
  }

  initialize() {
    // this.wifi = this.stateService.get(State.SCANNER_WIFI);
    // this.stateService.demoMode.subscribe((demoMode) => {
    //     this.demoMode = demoMode;
    // });
  }

  /**
   * Fetch the scanner's Wi-Fi name from the server.
   * If something goes wrong or if the result indicates any ambiguity, use the app's stored default value.
   *
   * @param serial A full serial number or any trailing substring of it.
   */
  async getAndSaveScannerWifi(serial: string) {
    const deviceId = serial.substring(serial.length - 4).toUpperCase(); // Set deviceId before asking for Wi-Fi name
    let wifi: string;
    let ambiguous = false;
    if (serial.includes('_')) {
      wifi = serial;
      return { deviceId, wifi, ambiguous };
    }
    try {
      const wifiNameResult = await this.apiMidService.getWiFiNameWithRetry(serial).toPromise();
      if (wifiNameResult?.count === 1) {
        // We have a unique result, so wifi_prefix is safe to use
        wifi = wifiNameResult.prefix + 'AP_' + deviceId;
      } else {
        if (wifiNameResult?.count > 1) {
          ambiguous = true;
        }
        // There are multiple results with possibly different wifi_prefix values or an error occurred. Stick to app's default.
        wifi = this.apiBasicService.getConfiguration().product + 'AP_' + deviceId.toUpperCase();
      }
    } catch (err) {
      wifi = `biozoomAP_${deviceId}`;
    }
    // todo: if request fails, return default wifi name (biozoomAP_0AAE, vegecheckAP_0AAE)
    // request from server. if unavailable, try to use given serial name if it satisfies certain standards
    // if not found or does not satisfy criteria, try 2-3 default cases
    return { deviceId, wifi, ambiguous };
    // await this.stateService.set(State.SCANNER_WIFI, this.wifi);
  }

  /**
   * Connects to the scanner WiFi, if not in USB mode or forced.
   * If parameter force is given, it will try to connect via WiFi disrespecting the USB connection.
   *
   * @param setState Method to set the global state of the application
   * @param state Object containing all persistent settings
   * @param force If true, it will ignore USB_MODE status and try to connect via WiFi.
   */
  async connectToScannerWifi(state: StateInterface): Promise<[boolean, string, number]> {
    let status;

    console.log('connect');

    const userAgent = navigator.userAgent;
    if (state.DEMO_MODE
      || this.platform.is('mobileweb')
      || (!(userAgent.includes('Android')) && !(userAgent.includes('iPhone')) && !(userAgent.includes('Mac OS')))
    ) {
      const to = this.platform.is('mobileweb') ? 1000 : 2000;
      await new Promise(resolve => setTimeout(resolve, to));
      return [true, '', 0];
    }

    if (this.platform.is('cordova') && state.SCANNER_WIFI) {
      try {
        try {
          const currentID = await this.wifiWizard2.getConnectedSSID();
          if (currentID === state.SCANNER_WIFI) {
            return [true, '', 0];
          }
        } catch (e) {
        }

        await this.requestPermissions();

        if (this.platform.is('ios')) {
          status = await this.wifiWizard2.iOSConnectNetwork(state.SCANNER_WIFI, this.password);
        } else if (this.platform.is('android')) {
          const r = await this.disconnectScanner(state);
          if (r) {
            await new Promise((r) => setTimeout(r, 1000));
          }
          status = await this.wifiWizard2.connect(state.SCANNER_WIFI, true, this.password, 'WPA');
        }
        return [status === true || status === 'NETWORK_CONNECTION_COMPLETED' ||
          status.toLowerCase() === state.SCANNER_WIFI.toLowerCase(), '', 0];

      } catch (e) {
        console.log('Catch:', e);
        return [false, '', 0];
      }
    }
    return [false, '', 0];
  }

  async requestPermissions() {
    if (this.platform.is('android')) {
      await this.androidPermissions.requestPermissions([
        this.androidPermissions.PERMISSION.CHANGE_NETWORK_STATE,
        this.androidPermissions.PERMISSION.CHANGE_WIFI_STATE,
        this.androidPermissions.PERMISSION.ACCESS_WIFI_STATE,
        this.androidPermissions.PERMISSION.ACCESS_COARSE_LOCATION,
        this.androidPermissions.PERMISSION.ACCESS_NETWORK_STATE,
        this.androidPermissions.PERMISSION.CHANGE_CONFIGURATION,
        this.androidPermissions.PERMISSION.WRITE_SETTINGS,
        this.androidPermissions.PERMISSION.ACCESS_FINE_LOCATION
      ]);
    }
  }

  async disconnectScanner(state: StateInterface, force = false) {
    let status = true;

    if (state.CONNECTION_TYPE === 'USB' && !force) {
      return true;
    }
    console.log('disconnect');

    if (state.DEMO_MODE) {
      await new Promise(resolve => setTimeout(resolve, 1));
      status = true;
    }
    if (this.platform.is('cordova')) {
      if (!state.SCANNER_WIFI) {
        status = false;
      }
      if (status && this.platform.is('ios')) {
        status = await this.wifiWizard2.iOSDisconnectNetwork(state.SCANNER_WIFI);
      } else if (status && this.platform.is('android')) {
        // TODO If disconnect is not working, an error should be displayed.
        status = await this.wifiWizard2.disconnect(state.SCANNER_WIFI).catch(() => false);
      }
    }

    return status;
  }

  waitForServerConnection(state: StateInterface, to: number) {
    let request: Observable<boolean>;
    let retries = to / 1000;

    if (state.DEMO_MODE) {
      request = of<boolean>(true).pipe(delay(2000));
      return request;
    }
    console.log('waitForInternet: Trying to reach server.');

    request = this.http.get(this.apiBasicService.getConfiguration().testConnectionUrl).pipe(
      // tap(console.log),
      map(
        () => true
      ),
      retryWhen(errors =>
        errors.pipe(
          // tap( val =>
          //     console.log('Retry. Val:' + val)
          // ),
          delay(1000),
          // tap(_ => {
          //     console.log('Retry now.');
          // }),
          mergeMap(error => {
            if (retries-- > 1) {
              return of(error);
            }
            return throwError(error);
          })
        )
      )
    );
    return request;
  }

  // getDeviceId(state: StateInterface) {
  //     return this.deviceId;
  // }
}
